import React, { useMemo, useState, useEffect, useCallback, useReducer } from 'react';
import {
	TranslatedString,
	formUpdateFieldValues,
	Dropdown,
	LocallySortedAndPaginatedTable,
	makeLocallySortedAndPaginatedTableColumnHeader
} from '@bosch/bt-ui';

import './CSRManagementPageTable.css';
import withErrorHandler from '../../components/ErrorHandler';
import CSRManagementTableButton from './CSRManagementTableButton';
import CSRManagementTableDesc from './CSRManagementTableDesc';
import CSRTimer from './CSRTimer';

const TABLE_HEADERS = [
	makeLocallySortedAndPaginatedTableColumnHeader('server', 'CSR Instance'),
	makeLocallySortedAndPaginatedTableColumnHeader('ipaddress', 'IP Address'),
	makeLocallySortedAndPaginatedTableColumnHeader('description', 'Description'),
	makeLocallySortedAndPaginatedTableColumnHeader('releasename', 'Current Release'),
	makeLocallySortedAndPaginatedTableColumnHeader('availablename', 'Replace With:'),
	makeLocallySortedAndPaginatedTableColumnHeader('submit', 'Deploy'),
	makeLocallySortedAndPaginatedTableColumnHeader('status', 'Status')
];

/* ************************************************************* */
// Special settings for Dropdown in the table
function csrInfoReducer(oldValues, update) {
	const {
		ipAddress,
		updateInfo
	} = update;

	return {
		...oldValues,
		[ipAddress]: formUpdateFieldValues(oldValues[ipAddress], updateInfo)
	};
}

function getInitialCSRInfo(returnResult) {
	const initialCsrInfo = {};

	for(let item of returnResult.data.info) {
		initialCsrInfo[item.ipAddress] = {releaseVersion: item.releaseVersion, oldReleaseVersion: item.releaseVersion, description: item.description, oldDescription: item.description, name: item.name, csrStatus: item.csrStatus};
	}

	return initialCsrInfo;
}
/* ************************************************************* */

function makeRow(token, isLoggedIn, task, csrInfo, updateCSRInfo, versions) {
	const {
		server,
		ipAddress
	} = task;

	return {
		key: ipAddress,
		columns: [
			{
				key: 'server',
				columnContent: server
			},
			{
				key: 'ipaddress',
				columnContent: ipAddress
			},
			{
				key: 'description',
				columnContent: <CSRManagementTableDesc 
					token={token} 
					isLoggedIn={isLoggedIn}
					ipAddress={ipAddress} 
					csrInfo={csrInfo}
					updateCSRInfo={updateCSRInfo} 
				/>
			},
			{
				key: 'releasename',
				columnContent: csrInfo[ipAddress].name
			},
			{
				key: 'availablename',
				columnContent:  <Dropdown
					fieldName="releaseVersion"
					options={versions}
					htmlAttributes={{
						/* ************************************************************* */
						// Special settings for Dropdown in the table
						onChange: (updateInfo) => {updateCSRInfo({ipAddress:ipAddress, updateInfo:updateInfo});},
						value: csrInfo[ipAddress].releaseVersion,
						disabled: csrInfo[ipAddress].csrStatus !== 'Succeeded' ? true : false
						/* ************************************************************* */
					}}
				/>
			},
			{
				key: 'submit',
				columnContent:  <CSRManagementTableButton 
					token={token} 
					isLoggedIn={isLoggedIn}
					ipAddress={ipAddress} 
					versions={versions} 
					csrInfo={csrInfo}
					updateCSRInfo={updateCSRInfo} 
				/>
			},
			{
				key: 'csrStatus',
				columnContent: <CSRTimer ComposedClass={csrInfo[ipAddress].csrStatus} token={token} isLoggedIn={isLoggedIn} ipAddress={ipAddress} csrInfo={csrInfo} updateCSRInfo={updateCSRInfo} />
			}
		],
		htmlAttributes: {
			className: (csrInfo[ipAddress].csrStatus === 'Running') ? 'csr-running-row' : (csrInfo[ipAddress].csrStatus === 'Succeeded') ? 'iccid-ok-row' : 'iccid-error-row'
		}
	};
}

const CSRManagementPageTable = (props) => {
	const {
		token,
		isLoggedIn,
		returnResult
	} = props;

	/* ************************************************************* */
	// Special settings for Dropdown in the table
	const [csrInfo, updateCSRInfo] = useReducer(csrInfoReducer, returnResult, getInitialCSRInfo);
	/* ************************************************************* */
	const [currentPage, setCurrentPage] = useState(1);

	const rows = useMemo(
		() => {
			const rows =  (returnResult?.data) ? returnResult.data.info.map(item => makeRow(token, isLoggedIn, item, csrInfo, updateCSRInfo, returnResult.data.version)) : [];
			return rows;
		},
		[token, isLoggedIn, returnResult, csrInfo]
	);

	return (
		<LocallySortedAndPaginatedTable
			headers={TABLE_HEADERS}
			rows={rows}
			numRowsPerPage={50}
			currentPage={currentPage}
			goToPageFunc={setCurrentPage}
			htmlAttributes={{
				className: 'csr-table'
			}}
			errorMessage="Error occured"
			noResultsMessage={<TranslatedString stringId="No items found" />}
			defaultSortColumn="server"
			defaultSortDirection="asc"
		/>
	);
};

export default withErrorHandler(React.memo(CSRManagementPageTable));